import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { EventoAbility } from '../eventos.ability';
import { MENU_ITEMS } from '../evento-menu';

/**
 * A sidebar do evento — a mesma de `eventos-insert-edit`, na pele nova.
 *
 * Mantém o que já existia: capa, nome, botão de recolher e os itens do
 * `MENU_ITEMS` na ordem atual (dashboard primeiro, "Ir para o evento" e
 * Configurações por último). O filtro por `item.permission` é AUTORIZAÇÃO
 * (CASL), não feature flag.
 *
 * Ícone via `mask-image` em vez de `<img>`: é o mesmo SVG de
 * `assets/icones/menu/`, mas pintado com `currentColor` — recolore sozinho no
 * dark e no estado ativo, sem precisar de uma segunda cópia do asset.
 */
const CHAVE_RECOLHIDA = 'evento-sidebar:recolhida';

/** `null` = usuário nunca escolheu; aí vale o padrão por largura. */
function lerPreferenciaRecolhida(): boolean | null {
  try {
    const salvo = localStorage.getItem(CHAVE_RECOLHIDA);
    return salvo === null ? null : salvo === 'true';
  } catch {
    return null;
  }
}

/**
 * 1600 é medido: abaixo disso a área útil da lista fica menor que a soma da
 * sidebar (276 + 20 de gap + 32 de padding) com a largura mínima da tabela de
 * Check-ins (~1530px com as colunas padrão).
 *
 * (A palavra que o Tailwind emitiria como utility proibida está evitada de
 * propósito aqui: o build varre os .ts do @source, comentário incluído.)
 */
function faltaEspacoParaSidebar(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 1600;
}

@Component({
  selector: 'app-evento-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  template: `
    <aside class="admin-portal-scope font-portal" [style.width.px]="recolhida() ? 68 : 276">
      <div class="sticky top-5 overflow-hidden rounded-card border border-border bg-card shadow-card">
        @if (!recolhida()) {
          <div class="relative aspect-video bg-muted">
            @if (fotoUrl()) {
              <img [src]="fotoUrl()" alt="" class="h-full w-full object-cover" />
            }
            <div class="absolute inset-0 bg-linear-to-t from-black/45 to-transparent"></div>
          </div>
        }

        <div
          [class]="
            recolhida()
              ? 'flex items-center justify-center border-b border-border p-2'
              : 'flex items-center gap-2 border-b border-border px-3.5 py-3'
          "
        >
          @if (!recolhida()) {
            <h2 class="m-0 min-w-0 flex-1 truncate font-display text-[15px]/[1.2] font-bold tracking-[0.01em]">
              {{ nome() || 'Evento' }}
            </h2>
          }
          <button
            type="button"
            class="inline-flex size-[30px] shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-primary hover:text-primary"
            (click)="alternarRecolhida()"
            [title]="recolhida() ? 'Expandir menu' : 'Recolher menu'"
            [attr.aria-label]="recolhida() ? 'Expandir menu' : 'Recolher menu'"
            [attr.aria-expanded]="!recolhida()"
          >
            <span class="text-sm">{{ recolhida() ? '›' : '‹' }}</span>
          </button>
        </div>

        <nav class="p-1.5" aria-label="Áreas do evento">
          @for (item of itens(); track item.page) {
            <a
              [routerLink]="rotaDe(item.page)"
              [queryParams]="queryDe(item.page)"
              [class]="
                (recolhida() ? 'flex items-center justify-center rounded-[10px] p-2.5 ' : 'flex items-center gap-2.5 rounded-[10px] px-2.5 py-2.5 ') +
                (item.page === paginaAtiva()
                  ? 'text-sm font-semibold no-underline bg-primary/10 text-primary'
                  : 'text-sm font-semibold no-underline text-muted-foreground hover:bg-muted hover:text-foreground')
              "
              [attr.aria-current]="item.page === paginaAtiva() ? 'page' : null"
              [attr.title]="recolhida() ? item.title : null"
            >
              <span
                class="size-4 shrink-0 bg-current"
                [style.mask]="'url(' + item.icon + ') center/contain no-repeat'"
                [style.-webkit-mask]="'url(' + item.icon + ') center/contain no-repeat'"
              ></span>
              @if (!recolhida()) {
                <span class="min-w-0 flex-1 truncate">{{ item.title }}</span>
              }
            </a>
          }
        </nav>
      </div>
    </aside>
  `,
})
export class EventoSidebarComponent {
  readonly idEvento = input.required<string>();
  readonly nome = input<string | null | undefined>('');
  readonly fotoUrl = input<string | null | undefined>('');
  readonly ability = input<EventoAbility | null>(null);
  /** `page` do MENU_ITEMS que está aberto. */
  readonly paginaAtiva = input<string>('checkins');

  /**
   * Recolhida ocupa 68px em vez de 276. Não é enfeite: numa tela de 1440 a
   * sidebar expandida deixa ~1112px para uma tabela que precisa de ~1530, e a
   * lista passa a rolar na horizontal. Recolher devolve 208px.
   *
   * Abaixo de 1600 nasce recolhida, porque ali o espaço não dá para as duas
   * coisas. A escolha do usuário vence esse padrão e é lembrada — antes ela se
   * perdia a cada reload, e recolher de novo virava rotina.
   */
  readonly recolhida = signal(lerPreferenciaRecolhida() ?? faltaEspacoParaSidebar());

  alternarRecolhida(): void {
    const nova = !this.recolhida();
    this.recolhida.set(nova);
    try {
      localStorage.setItem(CHAVE_RECOLHIDA, String(nova));
    } catch {
      /* localStorage indisponível */
    }
  }

  readonly itens = computed(() => {
    const ability = this.ability();
    return ability ? MENU_ITEMS.filter((i) => i.permission(ability)) : [];
  });

  /**
   * Só o check-in tem rota própria hoje. O resto continua sendo estado interno
   * do `eventos-insert-edit` — o `?page=` diz qual aba abrir, senão o clique
   * cairia na última aba visitada em vez da pedida.
   */
  rotaDe(page: string): unknown[] {
    return page === 'checkins'
      ? ['/private/meus-eventos', this.idEvento(), 'checkin']
      : ['/private/meus-eventos', this.idEvento()];
  }

  queryDe(page: string): Record<string, string> | null {
    return page === 'checkins' ? null : { page };
  }
}
